import { gxFormatDateDDMMMYYYY } from "@/app/utils/helpers";
import {
  Calendar,
  type CalendarChangeEvent,
} from "@progress/kendo-react-dateinputs";
import { Popup } from "@progress/kendo-react-popup";
import { useState, useRef, useEffect } from "react";
import "./DatePickerInput.scss";
type SupportedDateInput = string | Date;
interface DatePickerInputProps {
  id?: string;
  value: string;
  onChange: (date: string) => void;
  label?: string;
  validationMessage?: string;
  iconSrc?: string;
  placeholder?: string;
  disabled?: boolean;
  required?: boolean;
  minDate?: Date;
  maxDate?: Date;
  open?: boolean;
  /**
   * When true (default), dates before today are disabled in the calendar.
   * Set to false if you need to explicitly allow past dates.
   */
  disablePastDates?: boolean;
}

const DatePickerInput = ({
  id,
  value,
  onChange,
  label = "",
  placeholder = "DD-MMM-YYYY",
  iconSrc,
  validationMessage,
  required,
  disabled,
  minDate,
  maxDate,
  open = true,
  disablePastDates = true,
}: DatePickerInputProps) => {
  const [isFocused, setIsFocused] = useState(false);
  const [showCalendar, setShowCalendar] = useState(false);
  const [selectedDate, setSelectedDate] = useState<Date>();
  const wrapperRef = useRef<HTMLDivElement | null>(null);
  const popupRef = useRef<HTMLDivElement | null>(null);

  const [touched, setTouched] = useState<boolean>(false);
  const [internalError, setInternalError] = useState<string>("");

  // "Today" normalized to midnight, used as the default lower bound
  // so that previous dates are not selectable.
  const todayStart = (() => {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    return d;
  })();

  // If the caller passes an explicit minDate, respect it as-is.
  // Otherwise, when disablePastDates is true, fall back to today.
  const effectiveMinDate =
    minDate ?? (disablePastDates ? todayStart : undefined);

  // Click outside handler
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      if (
        wrapperRef.current &&
        popupRef.current &&
        !wrapperRef.current.contains(target) &&
        !popupRef.current.contains(target)
      ) {
        setShowCalendar(false);
        handleBlur();
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [touched, value]);

  // Handle focus
  const handleFocus = () => {
    setIsFocused(true);
    setTouched(true);
  };

  // Handle blur
  const handleBlur = () => {
    setIsFocused(false);

    // Only show internal error if field has been touched, is required, and has no value
    if (touched && required && (!value || value === "")) {
      setInternalError(`Please select ${label.toLowerCase() || "date"}`);
    } else {
      setInternalError("");
    }
  };

  // Handle calendar open/close
  const handleOnClick = () => {
    if (disabled || open === false) return;

    handleFocus();
    setShowCalendar(!showCalendar);
  };

  // Handle date selection
  const handleCalender = (e: CalendarChangeEvent<any>) => {
    const picked = e.value as Date | null;

    // Defensive guard: even though the Calendar's `min` prop should
    // prevent this, block programmatic/edge-case selection of a past date.
    // if (
    //   picked &&
    //   effectiveMinDate &&
    //   picked.getTime() < effectiveMinDate.getTime()
    // ) {
    //   setInternalError(
    //     `Please select a date on or after ${gxFormatDateDDMMMYYYY(effectiveMinDate)}`,
    //   );
    //   return;
    // }

    setTimeout(() => {
      setSelectedDate(picked ?? undefined);
    }, 300);
  };

  // Update selected date when calendar value changes
  useEffect(() => {
    if (selectedDate) {
      setIsFocused(true);
      setShowCalendar(false);
      onChange(`${selectedDate}`);

      //  Clear internal error when date is selected
      setInternalError("");
    }
  }, [selectedDate]);

  const parseDate = (value: SupportedDateInput): Date | null => {
    if (value instanceof Date && !Number.isNaN(value.getTime())) {
      return value;
    }

    if (typeof value !== "string") return null;

    // DD-MMM-YYYY (21-Jan-2026)
    const dmyMatch = new RegExp(/^(\d{2})-([A-Za-z]{3})-(\d{4})$/).exec(value);
    if (dmyMatch) {
      const [, day, mon, year] = dmyMatch;

      const months: Record<string, number> = {
        Jan: 0,
        Feb: 1,
        Mar: 2,
        Apr: 3,
        May: 4,
        Jun: 5,
        Jul: 6,
        Aug: 7,
        Sep: 8,
        Oct: 9,
        Nov: 10,
        Dec: 11,
      };

      const monthIndex = months[mon];
      if (monthIndex !== undefined) {
        return new Date(Number(year), monthIndex, Number(day));
      }
    }

    // YYYY-MM-DD
    const ymdMatch = new RegExp(/^(\d{4})-(\d{2})-(\d{2})$/).exec(value);
    if (ymdMatch) {
      const [, year, month, day] = ymdMatch;
      return new Date(Number(year), Number(month) - 1, Number(day));
    }

    // MM/DD/YYYY
    const mdyMatch = new RegExp(/^(\d{2})\/(\d{2})\/(\d{4})$/).exec(value);
    if (mdyMatch) {
      const [, month, day, year] = mdyMatch;
      return new Date(Number(year), Number(month) - 1, Number(day));
    }

    return null;
  };

  function parseAndValidateDate(value: SupportedDateInput) {
    const date = parseDate(value);
    return {
      isValid: () => date !== null && !Number.isNaN(date.getTime()),
      toDate: () => date,
    };
  }

  // Parse incoming value
  useEffect(() => {
    if (value && value !== "") {
      let parsedDate: Date | null = null;

      try {
        const nativeParsed = new Date(value);
        if (Number.isNaN(nativeParsed.getTime())) {
          const parsed = parseAndValidateDate(value);

          if (parsed.isValid()) {
            parsedDate = parsed.toDate();
          }
        } else {
          parsedDate = nativeParsed;
        }
      } catch {
        console.log("Invalid date format in DatePickerInput:", value);
      }

      if (parsedDate) {
        setSelectedDate(parsedDate);
      }
    } else {
      //  Clear selected date when value is cleared
      setSelectedDate(undefined);
    }
  }, [value]);

  //  Clear internal error when value becomes valid
  useEffect(() => {
    if (value && value !== "" && value !== undefined) {
      setInternalError("");
    }
  }, [value]);

  //  Show error only if there's a validation message OR internal error
  const hasError = !!(validationMessage || internalError);
  const errorMessage = validationMessage || internalError;

  return (
    <div id={id} ref={wrapperRef}>
      <button
        onFocus={handleFocus}
        className={`${disabled ? "gx-date-picker-input-wrapper-disable" : "gx-date-picker-input-wrapper"} ${hasError ? "border border-red-500!" : "border border-gray-300"} ${
          showCalendar ? "gx-open" : ""
        }`}
        onClick={handleOnClick}
      >
        {iconSrc && <img src={iconSrc} alt="calender_icon" />}
        {selectedDate ? (
          <input
            readOnly
            disabled={disabled}
            value={selectedDate ? gxFormatDateDDMMMYYYY(selectedDate) : ""}
            placeholder={isFocused && !value ? placeholder : ""}
            onFocus={handleFocus}
            onBlur={handleBlur}
            className="gx-date-picker-input"
          />
        ) : (
          <input
            value={""}
            readOnly
            disabled={disabled}
            placeholder={isFocused && !value ? placeholder : ""}
            onFocus={handleFocus}
            onBlur={handleBlur}
            className="gx-date-picker-input"
          />
        )}
        {label && (
          <label
            className={` ${
              required
                ? "gx-date-picker-label"
                : "gx-date-picker-label-required"
            } ${isFocused || !!value ? "float" : ""} ${
              showCalendar ? "gx-active" : ""
            }`}
          >
            {label}
            {required && <span className="gx-required-asterisk">*</span>}
          </label>
        )}
      </button>
      <Popup
        show={showCalendar}
        anchor={wrapperRef.current}
        popupClass="calendar-popup"
        anchorAlign={{ horizontal: "left", vertical: "bottom" }}
        popupAlign={{ horizontal: "left", vertical: "top" }}
      >
        <div ref={popupRef}>
          <Calendar
            value={selectedDate}
            onChange={handleCalender}
            min={effectiveMinDate}
            max={maxDate}
          />
        </div>
      </Popup>

      {hasError && errorMessage && (
        <p className="gx-input-validation-message  gx-font-regular">
          {errorMessage}
        </p>
      )}
    </div>
  );
};

export default DatePickerInput;
